import toast from "react-hot-toast";

// Link directo del test de un candidato, para no depender solo del correo de
// invitación: hay empresas cuyo filtro de correo lo retiene en cuarentena, y desde
// el panel se puede copiar o mandar por WhatsApp en segundos.

const FINISHED_STATUS = 4;

export const getTestLink = (collaboratorTestId) =>
  `${window.location.origin}/tests/start?id=${collaboratorTestId}`;

// El test vigente de una persona: el más reciente. La lista que llega del back no
// trae un orden garantizado, y usar el primero podía reenviar un test antiguo.
export const getLatestTest = (tests) =>
  [...(tests || [])]
    .filter((test) => test?.id)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];

export const isTestFinished = (test) => Number(test?.statusId) >= FINISHED_STATUS;

// Estado del correo de invitación según Amazon SES (columna emailStatus del test).
// problem: el correo no llegó y conviene mandar el link por otro medio.
// Ojo: "entregado" significa que el servidor del destinatario lo aceptó; un filtro
// de empresa puede igual dejarlo en cuarentena (para eso está "Sin abrir hace N días").
const EMAIL_STATUS_INFO = {
  // "delivered" de SES = el servidor del destinatario lo aceptó, no que esté en la
  // bandeja de entrada: un filtro de empresa puede dejarlo en spam o cuarentena.
  delivered: {
    label: 'Recibido por su servidor de correo',
    problem: false,
    note: 'Su servidor de correo lo aceptó, pero el filtro de su empresa puede haberlo dejado en spam o cuarentena. Si no abre el test, envíalo desde tu correo o por WhatsApp.',
  },
  // Enviado por Gmail desde la casilla real de la consultora (no por SES): no hay
  // evento de entrega; el detalle dice desde qué correo salió.
  mailbox: { label: 'Enviado desde el correo de la consultora', problem: false, fromMailbox: true },
  mailbox_reminder: { label: 'Recordatorio enviado desde el correo de la consultora', problem: false, fromMailbox: true },
  delayed: { label: 'Correo demorado', problem: false },
  transient_bounce: { label: 'Correo no entregado (temporal)', problem: true },
  bounced: { label: 'El correo rebotó', problem: true },
  rejected: { label: 'El correo fue rechazado', problem: true },
  complaint: { label: 'Marcado como spam', problem: true },
};

export const getEmailStatusInfo = (test) => {
  const info = EMAIL_STATUS_INFO[test?.emailStatus];
  if (!info) return null;
  const tip = info.problem
    ? `${test.emailStatusDetail ? `${test.emailStatusDetail}. ` : ''}Envíalo desde tu correo o por WhatsApp, o revisa que el correo esté bien escrito.`
    : info.note || test.emailStatusDetail || '';
  return { ...info, tip };
};

const copyWithFallback = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(textarea);
  return ok;
};

export const copyTestLink = async (collaboratorTestId) => {
  const link = getTestLink(collaboratorTestId);
  try {
    await navigator.clipboard.writeText(link);
    toast.success("Link copiado. Pégalo donde quieras enviarlo.");
  } catch (error) {
    if (copyWithFallback(link)) toast.success("Link copiado. Pégalo donde quieras enviarlo.");
    else toast.error(`No se pudo copiar automáticamente. Link: ${link}`, { duration: 15000 });
  }
};

// wa.me necesita el número con código de país y sin símbolos. Los teléfonos se
// guardan como +569XXXXXXXX, pero hay registros antiguos sin el 56 (9 dígitos).
export const normalizeWhatsAppPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 9 && digits.startsWith("9")) return `56${digits}`;
  return digits.length >= 10 ? digits : "";
};

// ── Enviar desde el correo propio de quien invita ─────────────────────────────
// Los correos automáticos (Amazon SES) pueden quedar en cuarentena en empresas con
// filtros estrictos (Microsoft 365 de Salmones Austral los aceptaba y los retenía).
// Un correo enviado desde la casilla de la consultora es un correo normal de persona
// a persona y pasa esos filtros. Se abre ya escrito: solo hay que apretar Enviar.
export const buildInvitationMessage = ({ name, link }) => {
  const firstName = name ? String(name).trim().split(' ')[0] : '';
  const subject = 'Tu invitación al Test GrowMe';
  const body =
    `Hola${firstName ? ` ${firstName}` : ''},\n\n` +
    'Te comparto el link para realizar tu evaluación en GrowMe:\n' +
    `${link}\n\n` +
    'Puedes responderla desde el computador o el celular. Tus respuestas se guardan a medida que avanzas, ' +
    'así que si se interrumpe puedes retomarla con el mismo link.\n\n' +
    'Cualquier duda, responde este correo.\n\n' +
    'Saludos,';
  return { subject, body };
};

// Recordatorio para quien aún no responde (mismo canal: el correo de la consultora).
export const buildReminderMessage = ({ name, link }) => {
  const firstName = name ? String(name).trim().split(' ')[0] : '';
  const subject = 'Recordatorio: tu Test GrowMe está pendiente';
  const body =
    `Hola${firstName ? ` ${firstName}` : ''},\n\n` +
    'Te recuerdo que aún tienes pendiente tu evaluación en GrowMe. Puedes responderla aquí:\n' +
    `${link}\n\n` +
    'Si ya la empezaste, el mismo link te lleva a donde quedaste: tus respuestas se guardan a medida que avanzas.\n\n' +
    'Cualquier duda, responde este correo.\n\n' +
    'Saludos,';
  return { subject, body };
};

const buildMessage = ({ kind, name, link }) =>
  (kind === 'reminder' ? buildReminderMessage : buildInvitationMessage)({ name, link });

export const EMAIL_PROVIDERS = ['gmail', 'outlook', 'app'];

export const buildEmailComposeUrl = ({ provider, email, subject, body }) => {
  const enc = encodeURIComponent;
  if (provider === 'gmail') return `https://mail.google.com/mail/?view=cm&fs=1&to=${enc(email || '')}&su=${enc(subject)}&body=${enc(body)}`;
  if (provider === 'outlook') return `https://outlook.office.com/mail/deeplink/compose?to=${enc(email || '')}&subject=${enc(subject)}&body=${enc(body)}`;
  return `mailto:${email || ''}?subject=${enc(subject)}&body=${enc(body)}`;
};

export const openEmailInvitation = ({ provider, collaboratorTestId, name, email, kind }) => {
  const { subject, body } = buildMessage({ kind, name, link: getTestLink(collaboratorTestId) });
  const url = buildEmailComposeUrl({ provider, email, subject, body });
  if (provider === 'app') window.location.href = url;
  else window.open(url, '_blank', 'noopener');
};

export const copyInvitationMessage = async ({ collaboratorTestId, name, kind }) => {
  const { subject, body } = buildMessage({ kind, name, link: getTestLink(collaboratorTestId) });
  const text = `Asunto: ${subject}\n\n${body}`;
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Mensaje copiado. Pégalo en tu correo.');
  } catch (error) {
    if (copyWithFallback(text)) toast.success('Mensaje copiado. Pégalo en tu correo.');
    else toast.error('No se pudo copiar el mensaje automáticamente.');
  }
};

// Acciones sobre el test vigente de una persona, desde cualquier lista del panel.
export const TEST_LINK_ACTIONS = ['resend', 'copy-link', 'whatsapp', 'my-email', 'my-email-reminder'];

// resend: resendInvitation de collaborators.actions (POST /collaborator_tests/send).
// openMyEmail: abre la ventana de envío (invitación o recordatorio) con el test y la persona.
export const runTestLinkAction = ({ action, tests, name, phone, email, resend, openMyEmail }) => {
  const latestTest = getLatestTest(tests);
  if (!latestTest?.id) {
    toast.error('Esta persona todavía no tiene un test asignado.');
    return;
  }
  if (isTestFinished(latestTest)) {
    toast('Esta persona ya respondió su test.', { icon: '✅' });
    return;
  }
  if (action === 'resend') {
    resend(latestTest.id, (res) => {
      if (res === 500) toast.error('No se pudo reenviar el correo. Prueba con Copiar link o WhatsApp.');
      else toast.success('Test reenviado por correo.');
    });
  }
  if (action === 'copy-link') copyTestLink(latestTest.id);
  if (action === 'whatsapp') openWhatsAppInvitation({ collaboratorTestId: latestTest.id, name, phone });
  if (action === 'my-email' && openMyEmail) openMyEmail({ collaboratorTestId: latestTest.id, name, email, kind: 'invitation' });
  if (action === 'my-email-reminder' && openMyEmail) openMyEmail({ collaboratorTestId: latestTest.id, name, email, kind: 'reminder' });
};

export const openWhatsAppInvitation = ({ collaboratorTestId, name, phone }) => {
  const greeting = name ? `Hola ${name.trim().split(" ")[0]}` : "Hola";
  const message =
    `${greeting}, te comparto el link para realizar tu evaluación en GrowMe:\n` +
    `${getTestLink(collaboratorTestId)}\n\n` +
    "Tus respuestas se guardan a medida que avanzas.";
  const number = normalizeWhatsAppPhone(phone);
  // Sin número, WhatsApp deja elegir el contacto.
  window.open(`https://wa.me/${number}?text=${encodeURIComponent(message)}`, "_blank", "noopener");
};
